import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';

import { fetchRewardHistory } from '../dailyRewards.service';

import { queryKeys } from 'hooks/queries/query-keys';

export type RewardHistoryItem = {
  id: number;
  date: string;
  calendarDay: string;
  reward: string;
};

export const useDailyRewardHistoryQuery = (page: number) => {
  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.dailyRewards.history(page),
    queryFn: () => fetchRewardHistory(page),
    placeholderData: keepPreviousData,
  });

  const displayItems = useMemo((): RewardHistoryItem[] => {
    if (!response) return [];

    return response.results.map((item) => ({
      id: item.id,
      date: format(parseISO(item.claimed_at), 'dd/MM/yyyy'),
      calendarDay: `Day ${item.day}`,
      reward: item.prize.name,
    }));
  }, [response]);

  const hasNextPage = response ? response.next !== null : false;
  const hasPreviousPage = response ? response.previous !== null : false;
  const pageSize = response && hasNextPage ? response.results.length : undefined;
  const totalPages = response
    ? pageSize
      ? Math.max(1, Math.ceil(response.count / pageSize))
      : page // on the last page, current page number = total pages
    : 1;

  return {
    displayItems,
    isEmpty: response ? response.count === 0 : true,
    showPagination: response ? response.count > response.results.length || hasPreviousPage : false,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    isLoading,
    error,
  };
};
